<template>
  <transition name="fade-embedded">
    <section v-if="options.show"
             class="scene-embedded-view"
             :class="{ backgroundMode: options.backgroundMode, frameMode: options.frameMode }">

      <div class="overlay" @click.exact="onClose"></div>

      <div class="container modal"
           :class="{
             fullPageMode: options.fullPageMode,
             'mode-object-preview': isObjectPreview
           }">

        <div class="content">
          <div v-if="isObjectPreview" class="object-preview-info">
            <h3 class="title">{{ options.config.title }}</h3>
            <div class="title-decoration"></div>
            <p v-if="options.config.description"
               class="description" v-html="options.config.description"></p>
          </div>

          <div ref="sceneEmbeddedViewportWrap"
               class="scene-embedded-viewport-wrap"
               :class="{ 'object-preview-container': isObjectPreview }"
               @sceneIsLoaded="onSceneIsLoaded"
               @panoramaClick="onPanoramaClick"
               @interactiveObjectClick="onInteractiveObjectClick"
               @interactiveObjectHover="onInteractiveObjectHover"
               @interactiveObjectLeave="onInteractiveObjectLeave"
               @sceneVideoPlay="onSceneVideoPlay"
               @sceneVideoPause="onSceneVideoPause"></div>

        </div>

        <transition name="fade">
          <div class="button action close" v-if="!isAnimating" @click="onClose">&times;</div>
        </transition>

        <transition name="slide">
          <div class="scene-embedded-list-wrap" v-if="isEmbeddedSceneListShowed">

            <scene-embedded-list :currentSceneId="currentScene.config.id"
                                 @embeddedSceneSelect="onEmbeddedSceneSelect"
                                 v-if="currentScene"/>

          </div>
        </transition>

        <scene-actions v-if="currentScene && currentScene.config.type === '360-video'"
                       @parameterChange="onAction"></scene-actions>

        <popup :options="popupOptions" @close="onPopupClose"/>

        <tooltip :options="tooltipOptions"/>

        <loader v-if="isLoading"/>

      </div>

    </section>
  </transition>
</template>

<script>
  import SceneEmbeddedList from './List'
  import Scene360          from '../../../scenes/Scene360Spheric'
  import Scene3DVista      from '../../../scenes/Scene3DVista'
  import SceneObjectView   from '../../../scenes/SceneObjectView'
  import Loader            from '../../Loader'
  import Popup             from '../../common/Popup'
  import Tooltip           from '../../common/Tooltip'
  import SceneHelpers      from '../../scene/Helpers'
  import SceneActions      from '../../scene/Actions'

  export default {
    name: 'SceneEmbeddedView',

    components: { Loader, SceneEmbeddedList, Popup, Tooltip, SceneHelpers, SceneActions },

    props: {
      options: {
        type: Object,
        default: () => {
          return {}
        }
      }
    },

    data() {
      return {
        isLoading: false,
        embedSceneList: [],
        currentScene: null,
        isEmbeddedSceneListShowed: false,
        isVistaScene: false,
        isAnimating: false,
        autoRotate: {
          enabled: false,
          paused: true
        },
        popupOptions: {
          styles: {},
          content: ''
        },
        tooltipOptions: {
          styles: {},
          content: ''
        }
      }
    },

    computed: {
      isObjectPreview() {
        return !!this.options.config && !!this.options.config.type && this.options.config.type === 'object'
      }
    },

    mounted() {
      if (this.options.config && this.options.show) {
        this.initCurrentScene(this.options.config)
      }
    },

    beforeDestroy() {
      if (this.currentScene) {
        this.currentScene.destroy()
      }
    },

    methods: {
      initCurrentScene(config) {
        if (this.currentScene && this.currentScene.config) {
          return
        }

        this.isLoading = true
        this.$nextTick(() => {
          if (this.currentScene) {
            this.currentScene.destroy()
            this.currentScene = null
            this.$refs.sceneEmbeddedViewportWrap.innerHTML = ''
          }

          if (config) {
            //window.Logger.log('Embedded view scene init config', config)
            this.isVistaScene = config.type === '3DVista'

            if (this.isVistaScene) {
              this.currentScene = new Scene3DVista(this.$refs.sceneEmbeddedViewportWrap)
            } else if (config.type === 'object') {
              this.currentScene = new SceneObjectView(this.$refs.sceneEmbeddedViewportWrap)
            } else {
              this.currentScene = new Scene360(this.$refs.sceneEmbeddedViewportWrap)
            }

            this.currentScene.setSceneConfig(config)
            this.currentScene.init()
          } else {
            throw new Error('Error embedded scene initialization. Config is not found.')
          }
        })
      },

      onSceneIsLoaded() {
        this.isLoading = false

        if (this.currentScene) {
          this.currentScene.updateSize()
        }
      },

      onClose() {
        this.isAnimating = true

        if (this.currentScene) {
          let leaveAnimation = Promise.resolve()

          if (typeof this.options.leaveAnimation === 'function') {
            leaveAnimation = this.options.leaveAnimation()
          }

          leaveAnimation.then(() => {
            this.currentScene.destroy()
            this.currentScene = null
            this.$refs.sceneEmbeddedViewportWrap.innerHTML = ''

            if (typeof this.options.leaveAnimation === 'function') {
              this.$emit('windowClose')
              this.isAnimating = false
            }
          })
        }
        this.popupReset()
        this.tooltipReset()

        if (typeof this.options.leaveAnimation !== 'function') {
          this.$emit('windowClose')
          this.isAnimating = false
        }
      },

      onEmbeddedSceneSelect(config) {
        this.initCurrentScene(config)
      },

      onInteractiveObjectClick(event) {
        this.popupOptions = {
          content: event.detail.data.content,
          hasCloseButton: true,
          styles: event.detail.data.styles || {}
        }
      },

      onPanoramaClick() {
        this.popupReset()
      },

      onInteractiveObjectHover(event) {
      },

      onInteractiveObjectLeave(event) {
      },

      onToggleHighlight() {},

      onToggleAutoRotateEnabled() {},

      onSceneVideoPlay(event) {
        // console.log('sceneVideoPlay', event, event.detail.originalEvent.type)
      },
      onSceneVideoPause(event) {
        // console.log('sceneVideoPaused', event, event.detail.originalEvent.type)
      },

      popupReset() {
        this.popupOptions = { content: '', styles: {} }
      },

      onPopupClose() {
        this.popupReset()
      },

      tooltipReset() {
        this.tooltipOptions = { content: '', styles: {} }
      },

      onAction(data) {
        if (data.hasOwnProperty('action')) {
          switch (data.action) {
            case 'videoPlay':
              this.currentScene.videoPlay()
              break
            case 'videoPause':
              this.currentScene.videoPause()
              break
            case 'videoVolume':
              if (data.hasOwnProperty('value') && !isNaN(parseInt(data.value))) {
                this.currentScene.videoVolumeSet(data.value)
              }
              break
          }
        }
      }
    },

    watch: {
      'options': function (value, prevValue) {
        if (value.show && value.config && (!this.currentScene || this.currentScene.config !== value.config)) {
          this.initCurrentScene(value.config)
        }

        let sizeChanges = (value.fullPageMode !== prevValue.fullPageMode)

        if (sizeChanges) {
          if (this.currentScene) {
            let interval = setInterval(() => {
              this.currentScene && this.currentScene.updateSize()
            }, 25)

            setTimeout(() => {
              clearInterval(interval)
            }, 600)
          }
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "../../../assets/styles/mixins";
  @import "../../../assets/styles/variables";
  @import "../../../assets/styles/main";

  .scene-embedded-view {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 2;

    .overlay {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
    }

    & > .container.modal {
      @include position-absolute-center();
      width: 100%;
      height: 100%;
      background-color: rgba(158, 158, 158, .3);
      @include border-radius(4px);
      overflow: hidden;

      @include transition(all .5s);

      &.fullPageMode {
        width: 100vw;
        height: 100vh;
        border-radius: 0;
      }

      .content {
        width: 100%;
        height: 100%;
        top: 50%;
        left: 50%;
        @include transform-translate(-50%, -50%);
        position: absolute;
        display: flex;

        .scene-embedded-viewport-wrap {
          width: 100%;
          height: 100%;
        }
      }

      &.mode-object-preview {
        width: 800px;
        height: 600px;

        .content {
          .object-preview-info {
            display: block;
            color: #fff;
            width: 50%;
            padding: 20px;

            .title {
              font-weight: 600;
              text-transform: uppercase;
            }

            .title-decoration {
              background-color: #fff;
              width: 100%;
              height: 2px;
            }
          }

          .object-preview-container {
            width: 50%;
          }
        }

        @include media("<tablet") {
          width: 100%;
          height: 100%;

          .content {
            display: block;

            .object-preview-info,
            .object-preview-container {
              width: 100%;
            }
          }
        }
      }

      .scene-embedded-list-wrap {
        background-color: #ccc;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
      }
    }

    &.backgroundMode {
      z-index: -1;
    }

    &.frameMode > .container.modal {
      width: 54vh;
      height: 54vh;
    }

    .button.close {
      cursor: pointer;
      position: absolute;
      top: 0;
      right: 0;
      z-index: 3;
      background: 0 0;
      color: transparent;
      white-space: nowrap;
      text-indent: 100%;
      height: 40px;
      width: 40px;
      @include transition(opacity .2s);

      &:after,
      &:before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 2px;
        height: 24px;
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
        background: rgba(255, 255, 255, 0.75);
        @include border-radius(2px);
        @include box-shadow(0 0 20px 4px rgba(0, 0, 0, 0.1));
      }

      &:before {
        transform: translateX(-50%) translateY(-50%) rotate(45deg);
      }

      &:after {
        transform: translateX(-50%) translateY(-50%) rotate(-45deg);
      }

      &:hover {
        &:before,
        &:after {
          background: #ffffff;
        }
      }
    }
  }
</style>
